import React, { useMemo, useState } from "react";
import { FiActivity, FiAlertTriangle, FiExternalLink } from "react-icons/fi";
import {
  formatCountdown,
  formatEventTime,
  formatUpdatedAgo,
  groupMacroEvents,
  isSnapshotStale
} from "./macroEventUtils";
import "./MacroEvents.css";

const sourceLabel = value => String(value || 'Unknown source').replace(/_/g, ' ');
const sourceTime = value => Number.isFinite(value) && value > 0
  ? new Date(value * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;

const EventRow = ({ event, now }) => {
  const time = formatEventTime(event);
  return (
    <article className={`macroEventRow macroImpact${event.impact}`}>
      <div className="macroEventTime">
        <strong>{time.primary}</strong>
        {event.timePrecision === "EXACT" && <span>{time.secondary}</span>}
        <span>{event.timePrecision === "EXACT" ? formatCountdown(event, now) : time.secondary}</span>
      </div>
      <div className="macroEventRegion">
        <strong>{event.country || "GLOBAL"}</strong>
        <span className={`macroImpactBadge macroImpactBadge${event.impact}`}>{event.impact}</span>
      </div>
      <div className="macroEventSummary">
        <strong>{event.title}</strong>
        <span>{event.cryptoRelevance}</span>
        {event.calendarOrigin && <span className="macroEventFallback">Research-verified schedule{event.stale ? ' · update overdue' : ''}</span>}
        {!event.calendarOrigin && event.stale && <span className="macroEventFallback">Retained schedule · update overdue</span>}
      </div>
      <div className="macroEventMarkets">
        <strong>{(event.affectedAssets || []).join(" · ")}</strong>
        <a href={event.sourceUrl} target="_blank" rel="noreferrer">
          Official source <FiExternalLink aria-hidden="true" />
        </a>
      </div>
    </article>
  );
};

const EventGroup = ({ title, events, now }) => {
  if (!events.length) return null;
  return (
    <section className="macroEventGroup">
      <h3>{title}</h3>
      <div className="macroEventRows">
        {events.map((event) => <EventRow key={event.id} event={event} now={now} />)}
      </div>
    </section>
  );
};

export default function MacroEventsTab({ snapshot, error, isLoading, now }) {
  const [showHigh, setShowHigh] = useState(true);
  const [showMedium, setShowMedium] = useState(true);
  const filteredEvents = useMemo(
    () => (snapshot?.events || []).filter((event) =>
      (showHigh && event.impact === "HIGH") || (showMedium && event.impact === "MEDIUM")),
    [snapshot, showHigh, showMedium]
  );
  const groups = useMemo(() => groupMacroEvents(filteredEvents, now), [filteredEvents, now]);
  const sourceEntries = Object.entries(snapshot?.sources || {}).map(([id, source]) => ({ ...source, source: source?.source || id }));
  const primarySources = sourceEntries.filter(source => source.source !== 'RESEARCH_CALENDAR');
  const failedSources = sourceEntries.filter((source) => source.healthy !== true);
  const researchEvents = (snapshot?.events || []).filter(event => event.calendarOrigin);
  const retainedEvents = (snapshot?.events || []).filter(event => event.stale && !event.calendarOrigin);
  const incompleteCoverage = failedSources.length > 0 || !primarySources.length;
  const sourceStatus = failedSources.length
    ? failedSources.map((source) => `${sourceLabel(source.source)}: ${source.error || "unavailable"}`).join("\n")
    : primarySources.length ? "Reported sources refreshed successfully" : "Primary calendar source coverage is unavailable";
  const stale = snapshot && isSnapshotStale(snapshot, now);

  if (isLoading) return <div className="macroState">Loading macro events...</div>;
  if (error && !snapshot) return <div className="macroState macroStateError">Macro events are not available yet.</div>;

  const hasEvents = groups.next24Hours.length > 0 || groups.laterThisWeek.length > 0;
  return (
    <div className="macroEventsTab">
      <header className="macroEventsHeader">
        <div>
          <h2>Macro Events</h2>
          <span>{formatUpdatedAgo(snapshot?.generatedAt, now)}</span>
        </div>
        <div className="macroEventsControls">
          <button
            type="button"
            className={showHigh ? "macroFilter macroFilterHigh macroFilterActive" : "macroFilter macroFilterHigh"}
            onClick={() => setShowHigh((value) => !value)}
            aria-pressed={showHigh}
          >
            High
          </button>
          <button
            type="button"
            className={showMedium ? "macroFilter macroFilterMedium macroFilterActive" : "macroFilter macroFilterMedium"}
            onClick={() => setShowMedium((value) => !value)}
            aria-pressed={showMedium}
          >
            Selected medium
          </button>
          <span className="macroSourceStatus" title={sourceStatus} aria-label="Macro event source status" tabIndex="0">
            <FiActivity aria-hidden="true" />
          </span>
        </div>
      </header>

      {incompleteCoverage && <section className="macroCoverageNotice" aria-label="Calendar coverage">
        <strong><FiAlertTriangle aria-hidden="true" /> Calendar coverage is incomplete</strong>
        <p>Failed or unreported feeds can leave events missing from this calendar.</p>
        {!primarySources.length && <p>Primary calendar feed status is unavailable.</p>}
        {!!failedSources.length && <ul>{failedSources.map(source => <li key={source.source}>
          <strong>{sourceLabel(source.source)}</strong>: {source.error || 'Feed unavailable'}
          <span>{sourceTime(source.lastSuccessfulAt) ? `Last successful refresh: ${sourceTime(source.lastSuccessfulAt)}.` : 'No successful refresh recorded.'}</span>
        </li>)}</ul>}
        <p>{researchEvents.length
          ? `${researchEvents.length} event${researchEvents.length === 1 ? '' : 's'} supplied by research-verified official schedules as fallback coverage. This does not confirm that failed feeds are complete.`
          : 'No research-verified fallback events are available in this snapshot.'}
          {retainedEvents.length ? ` ${retainedEvents.length} earlier feed event${retainedEvents.length === 1 ? ' is' : 's are'} retained and marked as overdue.` : ''}</p>
      </section>}
      {!incompleteCoverage && researchEvents.length > 0 && <p className="macroCoverageSupplement">Includes {researchEvents.length} research-verified official schedule event{researchEvents.length === 1 ? '' : 's'} supplementing the primary calendar.</p>}

      {stale && (
        <div className="macroStaleNotice">
          <FiAlertTriangle aria-hidden="true" /> Data may be stale. Last successful source data is retained for continuity.
        </div>
      )}

      {hasEvents ? (
        <>
          <EventGroup title="Next 24 Hours" events={groups.next24Hours} now={now} />
          <EventGroup title="Next 7 Days" events={groups.laterThisWeek} now={now} />
        </>
      ) : (
        <div className="macroState">{incompleteCoverage ? 'No qualifying events are available for the next seven days. Calendar coverage is incomplete.' : 'No qualifying macro events in the next seven days.'}</div>
      )}
    </div>
  );
}
